import { useState } from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import LockIcon from '@material-ui/icons/Lock';
import {
  ConfigValue,
  KeyRow,
  ServiceValues,
  configKeyMatrix,
  environmentLabel,
  environmentsWith,
  isEditableKey,
  isSecretLikeKey,
  isValidConfigKey,
} from '@internal/backstage-plugin-release-common';
import { Cell, Draft, cellKey } from './draft';

const useStyles = makeStyles(theme => ({
  key: {
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
  },
  // Long hosts and URLs wrap at any point rather than widening the column;
  // ordinary words stay whole.
  value: {
    fontFamily: 'monospace',
    overflowWrap: 'anywhere',
  },
  // The one difference that matters, made to stand out from the many that
  // are only environment naming.
  differs: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  missing: {
    color: theme.palette.error.main,
    fontStyle: 'italic',
  },
  empty: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  input: {
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
  },
}));

function Shown({ value, differs }: { value: ConfigValue | undefined; differs: boolean }) {
  const classes = useStyles();
  if (!value) {
    return (
      <Typography variant="body2" className={classes.missing}>
        missing
      </Typography>
    );
  }
  if (value.style === 'block') {
    return (
      <Tooltip title={value.value}>
        <Chip size="small" label="multi-line" />
      </Tooltip>
    );
  }
  if (value.value === '') {
    return (
      <Typography variant="body2" className={classes.empty}>
        (empty)
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      className={`${classes.value} ${differs ? classes.differs : ''}`}
    >
      {value.value}
    </Typography>
  );
}

interface Props {
  values: ServiceValues;
  workload: string;
  /** When set, cells become inputs and a variable can be added. */
  editing?: {
    draft: Draft;
    newKeys: string[];
    onDraft: (cell: Cell, value: string) => void;
    onAddKey: (key: string) => void;
  };
}

/**
 * Every configMap key of one workload, one column per environment.
 *
 * The point of the table is the cell that is not like the others: a key
 * missing from an environment, or a value that differs for a reason other
 * than the environment's own name. Both are marked; everything else is
 * plain, so the eye lands where it should. In edit mode the same cells take
 * input — a missing one takes the value that fixes the miss.
 */
export const ConfigMatrixTable = ({ values, workload, editing }: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const [pendingKey, setPendingKey] = useState('');
  const environments = environmentsWith(values, workload);

  const rows: KeyRow[] = [
    ...configKeyMatrix(values, workload),
    ...(editing?.newKeys ?? []).map(key => ({
      key,
      perEnvironment: {},
      missingIn: environments,
      differs: false,
      differsBeyondNaming: false,
    })),
  ];

  const stripe = (row: KeyRow): string | undefined => {
    if (row.missingIn.length > 0) return `inset 3px 0 0 ${theme.palette.error.main}`;
    if (row.differsBeyondNaming) return `inset 3px 0 0 ${theme.palette.warning.main}`;
    return undefined;
  };

  const editable = (row: KeyRow, environment: string): boolean =>
    Boolean(editing) &&
    isEditableKey(row.key, values) &&
    row.perEnvironment[environment]?.style !== 'block';

  const columns: TableColumn<KeyRow>[] = [
    {
      title: 'Key',
      field: 'key',
      // Keys are long but they are one word; the values are what wrap, and
      // in edit mode they are what is typed into. Give them the room.
      width: '28%',
      render: row => (
        <Box display="flex" alignItems="center" gridGap={4}>
          <span className={classes.key}>{row.key}</span>
          {!isEditableKey(row.key, values) && (
            <Tooltip title="Looks like it carries a secret; not editable from here.">
              <LockIcon fontSize="inherit" color="disabled" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    ...environments.map(
      (environment): TableColumn<KeyRow> => ({
        title: environmentLabel(environment),
        field: `perEnvironment.${environment}.value`,
        sorting: false,
        render: row => {
          if (!editing || !editable(row, environment)) {
            return (
              <Shown value={row.perEnvironment[environment]} differs={row.differsBeyondNaming} />
            );
          }
          const cell: Cell = { environment, workload, kind: 'configMap', key: row.key };
          const current = row.perEnvironment[environment]?.value;
          const drafted = editing.draft[cellKey(cell)];
          return (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={drafted ?? current ?? ''}
              placeholder={current === undefined ? 'missing — type a value to add it' : undefined}
              inputProps={{
                className: classes.input,
                'aria-label': `${row.key} in ${environmentLabel(environment)}`,
              }}
              onChange={event => editing.onDraft(cell, event.target.value)}
            />
          );
        },
      }),
    ),
  ];

  const trimmed = pendingKey.trim();
  const addProblem = ((): string => {
    if (!trimmed) return '';
    if (!isValidConfigKey(trimmed)) return 'Not a valid variable name.';
    if (isSecretLikeKey(trimmed)) return 'Looks like a secret; those go through the secrets plane.';
    if (rows.some(row => row.key === trimmed)) return 'Already listed.';
    return '';
  })();

  return (
    <>
      <Table
        options={{
          search: true,
          paging: false,
          toolbar: true,
          padding: 'dense',
          rowStyle: row => ({ boxShadow: stripe(row) }),
        }}
        columns={columns}
        data={rows}
        emptyContent={
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              No environment variables.
            </Typography>
          </Box>
        }
      />
      {editing && (
        <Box display="flex" alignItems="flex-start" gridGap={8} p={2}>
          <TextField
            size="small"
            variant="outlined"
            label="Add a variable"
            value={pendingKey}
            error={Boolean(addProblem)}
            helperText={addProblem || 'Set its value per environment above once added.'}
            inputProps={{ className: classes.input, 'aria-label': 'New variable name' }}
            onChange={event => setPendingKey(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && trimmed && !addProblem) {
                editing.onAddKey(trimmed);
                setPendingKey('');
              }
            }}
          />
          <Button
            variant="outlined"
            disabled={!trimmed || Boolean(addProblem)}
            onClick={() => {
              editing.onAddKey(trimmed);
              setPendingKey('');
            }}
          >
            Add
          </Button>
        </Box>
      )}
    </>
  );
};
